/**
 * NineSliceWindow - 9スライスウィンドウコンポーネント
 *
 * 小さな画像素材から任意サイズのウィンドウを生成する
 * Unity等のゲームエンジンで使われる9スライス技術をReactで実装
 *
 * Usage:
 * <NineSliceWindow variant="default" width={400}>
 *   <h2>タイトル</h2>
 *   <p>コンテンツ</p>
 * </NineSliceWindow>
 */

import React from 'react';
import { fallbackModalStyles, overlayStyles, borderRadius, zIndex as themeZIndex } from '../../styles';

// ========================================
// 型定義
// ========================================

export type WindowVariant = 'default' | 'battle' | 'shop' | 'menu' | 'dialog' | 'tooltip';

interface NineSliceConfig {
  imagePath: string;
  sliceSize: number; // 角のサイズ（px）
  imageSize: number; // 元画像のサイズ（px）
}

interface NineSliceWindowProps {
  children: React.ReactNode;
  variant?: WindowVariant;
  width?: number | string;
  height?: number | string;
  minWidth?: number | string;
  minHeight?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;
  padding?: number | string;
  style?: React.CSSProperties;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}

// ========================================
// バリアント設定
// ========================================

const VARIANT_CONFIGS: Record<WindowVariant, NineSliceConfig> = {
  default: {
    imagePath: '/images/ui/windows/window_black.png',
    sliceSize: 16,
    imageSize: 64,
  },
  battle: {
    imagePath: '/images/ui/window-battle.png',
    sliceSize: 16,
    imageSize: 48,
  },
  shop: {
    imagePath: '/images/ui/window-shop.png',
    sliceSize: 16,
    imageSize: 48,
  },
  menu: {
    imagePath: '/images/ui/window-menu.png',
    sliceSize: 16,
    imageSize: 48,
  },
  dialog: {
    imagePath: '/images/ui/window-dialog.png',
    sliceSize: 16,
    imageSize: 48,
  },
  tooltip: {
    imagePath: '/images/ui/window-tooltip.png',
    sliceSize: 8,
    imageSize: 24,
  },
};

// ========================================
// コンポーネント
// ========================================

export const NineSliceWindow: React.FC<NineSliceWindowProps> = ({
  children,
  variant = 'default',
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  padding = 16,
  style,
  className,
  onClick,
}) => {
  const config = VARIANT_CONFIGS[variant];
  const fallback = fallbackModalStyles[variant];

  // 9スライスのborder-imageスタイル
  const nineSliceStyle: React.CSSProperties = {
    borderStyle: 'solid',
    borderWidth: config.sliceSize,
    borderImage: `url(${config.imagePath}) ${config.sliceSize} fill / ${config.sliceSize}px / 0 stretch`,
    background: 'transparent',
  };

  // 画像が読み込めない場合のフォールバック
  const [useNineSlice, setUseNineSlice] = React.useState(true);

  React.useEffect(() => {
    // 画像の存在チェック
    const img = new Image();
    img.onload = () => setUseNineSlice(true);
    img.onerror = () => setUseNineSlice(false);
    img.src = config.imagePath;
  }, [config.imagePath]);

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    boxSizing: 'border-box',
    width,
    height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    padding,
    borderRadius: useNineSlice ? 0 : borderRadius.lg,
    ...(useNineSlice ? nineSliceStyle : fallback),
    ...style,
  };

  return (
    <div style={containerStyle} className={className} onClick={onClick}>
      {children}
    </div>
  );
};

// ========================================
// オーバーレイ付きモーダル用ラッパー
// ========================================

interface NineSliceModalProps extends NineSliceWindowProps {
  isOpen: boolean;
  onClose?: () => void;
  closeOnOverlayClick?: boolean;
  zIndex?: number;
}

export const NineSliceModal: React.FC<NineSliceModalProps> = ({
  isOpen,
  onClose,
  closeOnOverlayClick = true,
  zIndex = themeZIndex.modal,
  children,
  ...windowProps
}) => {
  // ESCキーで閉じる
  React.useEffect(() => {
    if (!isOpen || !onClose) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  return (
    <div
      style={{
        ...overlayStyles.default,
        zIndex,
      }}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <NineSliceWindow {...windowProps}>{children}</NineSliceWindow>
    </div>
  );
};

// ========================================
// エクスポート
// ========================================

export default NineSliceWindow;
